/** PWA — register the service worker on app boot.
 *
 *  Two reasons we register here (and not only inside `pushClient.js`):
 *    1. The PWA shell cache needs the SW registered for every visitor, not
 *       just those who opt into push notifications.
 *    2. Auto-update behaviour: when the SW finds a newer version of itself
 *       in the network, we activate it and refresh the page automatically
 *       so users always see the freshest build within minutes of a deploy.
 *
 *  Safe across environments:
 *    - No-op on browsers that don't support service workers (older Safari etc.)
 *    - No-op in dev/preview when window.location.protocol is "http:" except
 *      localhost — service workers require HTTPS in production but CRA dev
 *      serves the SW just fine.
 */
const SW_URL = "/sw.js";

/** Show a tiny non-blocking toast, then reload after `delayMs`. Falls
 *  back to a plain confirm() if Sonner isn't loaded yet. */
async function _reloadWithToast() {
  // Guard against reload loops: mark the reload in sessionStorage so we
  // only auto-reload once per SW-update event even if the browser re-
  // fires controllerchange in quick succession.
  const KEY = "pys.sw.reloading";
  try {
    if (sessionStorage.getItem(KEY) === "1") return;
    sessionStorage.setItem(KEY, "1");
  } catch (_) {
    /* private mode — proceed without the guard */
  }
  try {
    // Lazy-import so the SW registration path stays synchronous on boot.
    const { toast } = await import("sonner");
    toast("Fresh PYS available — reloading…", { duration: 3000 });
  } catch (_) {
    /* toast unavailable — reload silently */
  }
  // Give the toast a beat, then hard-reload so cache-first chunks pick
  // up the new hashed filenames from the newly-activated SW.
  setTimeout(() => {
    try {
      console.info("[PYS] auto-reload for fresh build");
    } catch (_) {
      /* no console */
    }
    window.location.reload();
  }, 800);
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Reload once the new SW takes control of the page — this is the
  // canonical signal that a fresh build is live. Fires exactly once
  // per SW-activation cycle.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    _reloadWithToast();
  });

  // Run after `load` so the SW registration doesn't compete with first paint.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SW_URL, { scope: "/" })
      .then((registration) => {
        // Ping the SW immediately to see if a fresher version already
        // shipped while the user was away. Then re-check every 2 min
        // so users on the site during a deploy pick it up fast.
        registration.update().catch(() => {});
        setInterval(() => {
          registration.update().catch(() => {});
        }, 2 * 60 * 1000);

        const activateWaiting = () => {
          const waiting = registration.waiting;
          if (waiting) {
            // Ask the waiting SW to skipWaiting → it takes control
            // → controllerchange fires → we reload.
            waiting.postMessage({ type: "SKIP_WAITING" });
          }
        };

        // If there's already a waiting SW at page-load, activate now.
        if (registration.waiting && navigator.serviceWorker.controller) {
          activateWaiting();
        }

        registration.addEventListener("updatefound", () => {
          const newSW = registration.installing;
          if (!newSW) return;
          newSW.addEventListener("statechange", () => {
            if (
              newSW.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // A new version is installed and waiting — flip the switch.
              activateWaiting();
            }
          });
        });
      })
      .catch((err) => {
        console.warn("[PYS] SW registration failed", err);
      });
  });
}
